import {
  TypeIcon,
  StatusIcon,
  AssigneeAvatar,
  NameLabel,
  ProgressRateLabel,
} from './ganttSliderStyles'
import { LabelMode, WbsItemForDisplay } from './useLabelMode'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import {
  getWbsItemStatusIconColor,
  getWbsItemStatusLabel,
} from '../../../../../../../../../lib/functions/wbsItem'

type GanttLabelContentProps = {
  isSummary: boolean
  displayOptions?: GanttDisplayOptions
  wbsItemForDisplay?: WbsItemForDisplay
  progressRate: number
  labelMode: LabelMode
  hasCumulation: boolean
}

/** ラベル表示対象が1つでもあるかを判定する */
export const hasAnyContent = ({
  isSummary,
  displayOptions,
  wbsItemForDisplay,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'isSummary' | 'displayOptions' | 'wbsItemForDisplay' | 'hasCumulation'
>): boolean => {
  const hasTypeIcon =
    !isSummary &&
    !!displayOptions?.showTypeIcon &&
    !!wbsItemForDisplay?.wbsItemType?.iconUrl
  const hasStatus =
    !isSummary && !!displayOptions?.showStatus && !!wbsItemForDisplay?.status
  const hasName = !!displayOptions?.showName && !!wbsItemForDisplay?.displayName
  const hasAssignee =
    !isSummary &&
    !!displayOptions?.showAssignee &&
    !!wbsItemForDisplay?.responsible
  const hasProgressRate = !!displayOptions?.showProgressRate && hasCumulation
  return hasTypeIcon || hasStatus || hasName || hasAssignee || hasProgressRate
}

/** ホバー用ツールチップテキストを組み立てる */
export const buildHoverText = ({
  wbsItemForDisplay,
  progressRate,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'wbsItemForDisplay' | 'progressRate' | 'hasCumulation'
>): string =>
  [
    wbsItemForDisplay?.status
      ? getWbsItemStatusLabel(wbsItemForDisplay.status)
      : undefined,
    wbsItemForDisplay?.displayName,
    wbsItemForDisplay?.responsible?.name,
    hasCumulation ? `${(progressRate * 100).toFixed(1)}%` : undefined,
  ]
    .filter(Boolean)
    .join(' / ')

/** ラベル内のコンテンツ（ステータスアイコン・名前・担当者・進捗率） */
export const GanttLabelContent = ({
  isSummary,
  displayOptions,
  wbsItemForDisplay,
  progressRate,
  labelMode,
  hasCumulation,
}: GanttLabelContentProps) => {
  const hasTypeIcon =
    !isSummary &&
    !!displayOptions?.showTypeIcon &&
    !!wbsItemForDisplay?.wbsItemType?.iconUrl
  const hasStatus =
    !isSummary && !!displayOptions?.showStatus && !!wbsItemForDisplay?.status
  const hasName = !!displayOptions?.showName && !!wbsItemForDisplay?.displayName
  const hasAssignee =
    !isSummary &&
    !!displayOptions?.showAssignee &&
    !!wbsItemForDisplay?.responsible
  const hasProgressRate = !!displayOptions?.showProgressRate && hasCumulation

  return (
    <>
      {hasTypeIcon && wbsItemForDisplay?.wbsItemType?.iconUrl && (
        <TypeIcon src={wbsItemForDisplay.wbsItemType.iconUrl} />
      )}
      {hasStatus && wbsItemForDisplay?.status && (
        <StatusIcon
          color={getWbsItemStatusIconColor(wbsItemForDisplay.status)}
        />
      )}
      {hasName && wbsItemForDisplay?.displayName && (
        <NameLabel inside={labelMode === 'inside'} isSummary={isSummary}>
          {wbsItemForDisplay.displayName}
        </NameLabel>
      )}
      {hasAssignee && wbsItemForDisplay?.responsible && (
        <AssigneeAvatar
          src={wbsItemForDisplay.responsible.iconUrl}
          alt={wbsItemForDisplay.responsible.name}
        >
          {wbsItemForDisplay.responsible.name?.charAt(0)}
        </AssigneeAvatar>
      )}
      {hasProgressRate && (
        <ProgressRateLabel
          inside={labelMode === 'inside'}
          isSummary={isSummary}
          showSeparator={hasStatus || hasName || hasAssignee}
        >
          {(progressRate * 100).toFixed(1)}%
        </ProgressRateLabel>
      )}
    </>
  )
}
